import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Storage as StorageIcon,
  Inventory as InventoryIcon,
  Key as KeyIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import type { Account } from '@/types'

interface Props {
  account: Account
  backendsCount: number
  virtualBucketsCount: number
  credentialsCount: number
  membersCount: number
}

interface StatCardProps {
  title: string
  count: number
  href: string
  icon: React.ReactNode
  color: string
}

function StatCard({ title, count, href, icon, color }: StatCardProps) {
  return (
    <Paper
      component={Link}
      href={href}
      sx={{
        p: 2.5,
        display: 'block',
        textDecoration: 'none',
        transition: 'box-shadow 0.15s ease-in-out',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ ml: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {count}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}

export default function AccountDashboard({
  account,
  backendsCount,
  virtualBucketsCount,
  credentialsCount,
  membersCount,
}: Props) {
  const { isAccountAdmin } = usePage<{ isAccountAdmin: boolean }>().props

  const stats: StatCardProps[] = [
    {
      title: 'Backends',
      count: backendsCount,
      href: `/accounts/${account.slug}/backends`,
      icon: <StorageIcon sx={{ color: 'white' }} />,
      color: '#6366F1',
    },
    {
      title: 'Virtual Buckets',
      count: virtualBucketsCount,
      href: `/accounts/${account.slug}/backends`,
      icon: <InventoryIcon sx={{ color: 'white' }} />,
      color: '#10B981',
    },
    {
      title: 'Credentials',
      count: credentialsCount,
      href: `/accounts/${account.slug}/credentials`,
      icon: <KeyIcon sx={{ color: 'white' }} />,
      color: '#F59E0B',
    },
    {
      title: 'Team Members',
      count: membersCount,
      href: `/accounts/${account.slug}/members`,
      icon: <PeopleIcon sx={{ color: 'white' }} />,
      color: '#8B5CF6',
    },
  ]

  const quickActions = [
    {
      title: 'Add Backend',
      description: 'Connect a new S3-compatible storage',
      href: `/accounts/${account.slug}/backends/new`,
    },
    {
      title: 'Create Credential',
      description: 'Generate new API credentials',
      href: `/accounts/${account.slug}/credentials/new`,
    },
    {
      title: 'Add Team Member',
      description: 'Invite someone to this account',
      href: `/accounts/${account.slug}/members/new`,
    },
  ]

  const gettingStartedSteps = [
    { title: 'Add a Backend', description: 'Connect your S3-compatible storage (AWS S3, MinIO, etc.)' },
    { title: 'Create Virtual Buckets', description: 'Define virtual buckets with path prefixes' },
    { title: 'Generate Credentials', description: 'Create API keys and assign them to virtual buckets' },
    { title: 'Start Using the API', description: 'Use your credentials with any S3 client' },
  ]

  return (
    <ManagementLayout>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview of {account.name}
          </Typography>
        </Box>
        {isAccountAdmin && (
          <Button
            component={Link}
            href={`/accounts/${account.slug}/edit`}
            variant="outlined"
            startIcon={<EditIcon />}
          >
            Edit Account
          </Button>
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} lg={3} key={stat.title}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper>
            <Box sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Quick Actions
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {isAccountAdmin ? (
                <List disablePadding>
                  {quickActions.map((action, index) => (
                    <ListItemButton
                      key={action.title}
                      component={Link}
                      href={action.href}
                      sx={{
                        border: 1,
                        borderColor: 'grey.200',
                        borderRadius: 1,
                        mb: index < quickActions.length - 1 ? 2 : 0,
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <AddIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={action.title}
                        secondary={action.description}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ fontSize: '0.875rem' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Contact an admin to manage backends, credentials, or team members.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper>
            <Box sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Getting Started
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {gettingStartedSteps.map((step, index) => (
                  <Box
                    key={step.title}
                    component="li"
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: index < gettingStartedSteps.length - 1 ? 2 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.50',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ ml: 1.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </ManagementLayout>
  )
}
